/**
 * CommHubIndicator - Subtle status indicator for Comm Hub connectivity
 *
 * Drop this file into any Electron app's src/components/ directory.
 * Also requires useCommHub.js in src/hooks/.
 *
 * Usage:
 *   import CommHubIndicator from './components/CommHubIndicator';
 *   // In your App component:
 *   <CommHubIndicator project="fire-pos" />
 */
import React, { useState } from 'react';
import { useCommHub } from '../hooks/useCommHub';

const styles = {
  container: {
    position: 'fixed',
    bottom: 12,
    right: 12,
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    borderRadius: 20,
    fontSize: 11,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    userSelect: 'none',
    WebkitAppRegion: 'no-drag',
  },
  dot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    flexShrink: 0,
  },
  tooltip: {
    position: 'fixed',
    bottom: 44,
    right: 12,
    zIndex: 10000,
    padding: '8px 12px',
    borderRadius: 8,
    fontSize: 11,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    lineHeight: 1.5,
    minWidth: 160,
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
  },
};

export default function CommHubIndicator({ project = '' }) {
  const { connected, pendingTasks, activeAgents, councilSessions } = useCommHub(project);
  const [showTooltip, setShowTooltip] = useState(false);

  const dotColor = connected ? '#22c55e' : '#6b7280';
  const bgColor = connected ? 'rgba(34,197,94,0.08)' : 'rgba(107,114,128,0.08)';
  const borderColor = connected ? 'rgba(34,197,94,0.15)' : 'rgba(107,114,128,0.15)';
  const textColor = connected ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.4)';

  const label = connected
    ? pendingTasks > 0 ? `${pendingTasks}` : ''
    : '';

  return (
    <>
      <div
        style={{
          ...styles.container,
          background: bgColor,
          border: `1px solid ${borderColor}`,
          color: textColor,
        }}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        title={connected ? 'Comm Hub connected' : 'Comm Hub disconnected'}
      >
        <div
          style={{
            ...styles.dot,
            backgroundColor: dotColor,
            boxShadow: connected ? `0 0 6px ${dotColor}` : 'none',
          }}
        />
        {label && <span>{label}</span>}
      </div>

      {showTooltip && (
        <div
          style={{
            ...styles.tooltip,
            background: 'rgba(15,15,30,0.95)',
            border: '1px solid rgba(255,255,255,0.1)',
            color: 'rgba(255,255,255,0.85)',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            Comm Hub {connected ? 'Connected' : 'Disconnected'}
          </div>
          {connected ? (
            <>
              <div>Pending tasks: {pendingTasks}</div>
              <div>Active agents: {activeAgents}</div>
              {councilSessions > 0 && (
                <div>Council sessions: {councilSessions}</div>
              )}
            </>
          ) : (
            <div style={{ color: 'rgba(255,255,255,0.5)' }}>
              Reconnecting...
            </div>
          )}
        </div>
      )}
    </>
  );
}
